
import { Component, Emit, Prop, Ref, Vue } from 'vue-property-decorator'
import { Observer } from 'mobx-vue'
import EmblaCarousel, { type EmblaCarouselType, type EmblaOptionsType } from 'embla-carousel'
import LazyImg from '@xt/www/components/lazyImg/index.vue'
import type { Content } from '@xt/client/entities/course-v2/user-content'
import { WatermarkCanvas, getFileNameAndExt, loadImage } from '@xt/client/utils/watermark/index'
import { contextMenuManager } from '@xt/client/mixins/components/directives/viewer'
import { reportEntryLog } from '@xt/client/utils/log'
import { saveAs } from 'file-saver'
import GlobalContextMenu, { type ShowOptions } from '@xt/www/components/contextMenu/index.vue'
import Teleport from '@xt/client/mixins/components/teleport.vue'
import { getShortMemberId } from '@xt/client/utils/member-id/index'

const OPTIONS: EmblaOptionsType = { loop: false, duration: 30 }

type ComputedUrlProps = {
  /** 原图url */
  originalUrl: string
  /** 预览图url */
  viewUrl: string
  /** 图片类型 */
  type: 'high' | 'wide' | 'normal'
  /** 图片盒子宽度 */
  containerWidth?: number
  /** 图片盒子高度 */
  containerHeight?: number
  /** 图片原始宽度 */
  originalWidth: number
  /** 图片原始高度 */
  originalHeight: number
}

@Observer
@Component({
  components: { LazyImg, GlobalContextMenu, Teleport }
})
export default class ContentSwiper extends Vue {
  @Ref()
  contentSwiper: HTMLDivElement

  @Ref()
  ctxMenu: {
    visible: boolean
    showAt: (options: ShowOptions) => void
  }

  @Prop({ required: true }) imageList: Content['imageList']
  // 内容发布人
  @Prop({ required: true }) ownerId: string

  current = 0

  emblaApi: EmblaCarouselType

  modalWidth: number = 712
  modalHeight: number = 0

  // 限制宽高
  modalLimitWidth = 200
  modalLimitHeight = 200

  // 下载中遮罩
  downloading: boolean = false
  // 菜单亮起时展示透明蒙层，避免和其他点击事件冲突
  showMenu: boolean = false
  // 重新处理之后的图片数据
  computedImageList: ComputedUrlProps[] = []

  // 弹窗右键菜单展示
  showContextMenu(e: MouseEvent, item: ComputedUrlProps) {
    this.showMenu = true
    this.ctxMenu.showAt({
      x: e.clientX,
      y: e.clientY,
      label: '下载',
      onClick: () => {
        this.handleDownload(item)
      }
    })
  }

  // 图片css裁剪样式
  getImageItemStyle(i: ComputedUrlProps) {
    if (i.type === 'high') {
      return {
        width: `${this.modalLimitWidth}px`,
        height: `${i.containerHeight}px`
      }
    } else if (i.type === 'wide') {
      return {
        width: `${i.containerWidth}px`,
        height: `${this.modalLimitHeight}px`
      }
    }
  }

  // 判断是否超出比例
  getIsProportion(oWidth: number, oHeight: number): boolean {
    const baseR: number = 5
    return Math.ceil(Math.max(oWidth, oHeight) / Math.min(oWidth, oHeight)) > baseR
  }

  // 图片链接获取逻辑
  getPicUrl(item: { originalWidth: number; originalHeight: number; originalUrl: string }): string {
    const { originalWidth: oWidth, originalHeight: oHeight, originalUrl: url } = item
    const factor: number = 2
    const baseWidth: number = 712
    const baseHeight: number = 1364

    if ((oWidth < baseWidth * factor && oHeight < baseHeight * factor) || this.getIsProportion(oWidth, oHeight))
      return url + '?x-oss-process=image/quality,q_90'

    return url + '?x-oss-process=image/resize,m_lfit,' + `w_${baseWidth * factor},` + `h_${baseHeight * factor}` + '/quality,q_90'
  }

  // 图片数据处理
  filterImage(o: { originalWidth: number; originalHeight: number; originalUrl: string }) {
    // 计算宽高比
    const ratio = Math.floor((o.originalWidth / o.originalHeight) * 100) / 100
    const isHigh = ratio < 0.5 // 宽高比小于 0.5
    const isWide = ratio > 2 // 宽高比大于 2

    const item: ComputedUrlProps = {
      viewUrl: this.getPicUrl(o),
      originalUrl: o.originalUrl,
      type: 'normal',
      originalWidth: o.originalWidth,
      originalHeight: o.originalHeight
    }

    // console.log('filterImage modalWidth modalHeight', this.modalWidth, this.modalHeight)

    if (isHigh) {
      const shouldHeigth = Math.floor(this.modalLimitWidth / ratio)
      if (this.modalHeight < shouldHeigth) {
        item.type = 'high'
        item.containerHeight = Math.ceil(this.modalLimitWidth / (o.originalWidth / o.originalHeight))
        return item
      }
    } else if (isWide) {
      const shouldWidth = Math.floor(this.modalLimitHeight * ratio)
      if (this.modalWidth < shouldWidth) {
        item.type = 'wide'
        item.containerWidth = Math.ceil(this.modalLimitHeight / (o.originalHeight / o.originalWidth))
        return item
      }
    }

    return item
  }

  // 下载操作
  async handleDownload(item: ComputedUrlProps) {
    const { originalWidth: oWidth, originalHeight: oHeight, viewUrl } = item
    const { name: filename } = getFileNameAndExt(viewUrl)
    const minL: number = 500
    let finalUrl: string = ''

    console.log('原图参数', 'oWidth:', oWidth, 'oHeight:', oHeight)

    try {
      this.downloading = true
      // 小图 || 比例超大图
      if (oWidth <= minL || oHeight <= minL || this.getIsProportion(oWidth, oHeight)) {
        finalUrl = viewUrl + '&download=true'
        // console.log('小图链接: ', finalUrl)
      } else {
        // console.log('水印底图链接 水印文字: ', viewUrl + '&cache=false', this.ownerId, getShortMemberId(this.ownerId))
        finalUrl = await this.getWatermarkUrl(viewUrl + '&cache=false', getShortMemberId(this.ownerId))
      }

      if (!finalUrl) return

      saveAs(finalUrl, filename)
    } catch (err) {
      console.log('下载失败: 请查看控制台日志!', err)
      reportEntryLog('content.download.fail', err)
    } finally {
      this.downloading = false
    }
  }

  // 获取添加完水印的图片
  async getWatermarkUrl(url: string, watermarkText: string): Promise<string> {
    try {
      const img = await loadImage(url)
      const { naturalWidth: watermarkWidth, naturalHeight: watermarkHeight } = img
      const logoUrl = this.$store.$global.static + '/images/water-markv9.png'
      const paddingRadio = 0.5 // 整体水印与边缘的间距比例
      const watermarkRadio = 0.01 // 水印与图片的间距比例
      const minTextFont = 8 // 最小字体
      const maxTextFont = 10 // 最大字体
      const base = 200 // 基准值
      const longEdge = Math.max(watermarkWidth, watermarkHeight) // 长边
      const textFont = Math.min(Math.max(Math.ceil(longEdge / base), minTextFont), maxTextFont)
      // 比例计算值
      const radioConfig = {
        sideSpace: Math.ceil(textFont * paddingRadio),
        picTextSpace: textFont * watermarkRadio,
        comOpacity: 0.3,
        watermarkTextFont: `${textFont}px Arial`
      }

      const wm = new WatermarkCanvas({
        width: watermarkWidth,
        height: watermarkHeight
      })
      const { width: i_w, height: i_h } = wm.measureTextSize(watermarkText, {
        fontSize: textFont,
        lineHeight: Math.floor(textFont * 1.2)
      })

      console.log(
        '字号 文字长度 文字高度 边缘间距 水印间距 图片长度 图片高度',
        textFont,
        i_w,
        Math.floor(i_h),
        radioConfig.sideSpace,
        radioConfig.picTextSpace,
        watermarkWidth,
        watermarkHeight
      )

      const watermarks = [
        // 图片水印：指定宽度，保留纵横比，放在右下角，距离边缘 32px
        {
          type: 'image' as const,
          imageSrc: logoUrl,
          imageWidth: Math.ceil(i_w),
          position: 'bottom-right' as const,
          offsetX: radioConfig.sideSpace,
          offsetY: i_h + radioConfig.sideSpace + radioConfig.picTextSpace,
          opacity: 1,
          rotation: 0
        },
        {
          type: 'text' as const,
          text: watermarkText,
          font: radioConfig.watermarkTextFont,
          color: 'rgba(255,255,255)',
          position: 'bottom-right' as const,
          offsetX: radioConfig.sideSpace,
          offsetY: radioConfig.sideSpace,
          opacity: radioConfig.comOpacity,
          rotation: 0
        }
      ]
      const canvas = await wm.drawBackgroundAndWatermarks(img, watermarks)
      const { imgExt } = getFileNameAndExt(url)
      const finalUrl = canvas.toDataURL(imgExt === 'png' ? 'image/png' : 'image/jpeg')
      return finalUrl
    } catch (err) {
      console.log('绘制失败: 请查看控制台日志!', err)
      reportEntryLog('content.get-watermark-pic.fail', err)
      return ''
    }
  }

  handleScroll(emblaApi: EmblaCarouselType) {
    this.current = emblaApi.selectedScrollSnap()
  }

  onImagePreview(idx: number) {
    this.$showViewer(
      this.computedImageList.map(v => v.viewUrl),
      idx
    )

    this.$nextTick(() => {
      contextMenuManager.subscribe((e: MouseEvent, current: number) => {
        const { originalWidth, originalHeight, originalUrl } = this.computedImageList[current]

        console.log('预览图点击下载: width, height, url', originalWidth, originalHeight, originalUrl)

        const item = this.filterImage({
          originalWidth,
          originalHeight,
          originalUrl
        })

        if (!this.ctxMenu) return

        this.ctxMenu.showAt({
          x: e.clientX,
          y: e.clientY,
          label: '下载',
          onClick: () => {
            this.handleDownload(item)
          }
        })
      })
    })
  }

  handlePrev() {
    if (!this.emblaApi) return

    this.emblaApi.scrollPrev()
  }

  handleNext() {
    if (!this.emblaApi) return

    this.emblaApi.scrollNext()
  }

  created() {}
  mounted() {
    if (!this.contentSwiper) return

    this.emblaApi = EmblaCarousel(this.contentSwiper, OPTIONS)

    this.emblaApi.on('select', this.handleScroll)

    const u = document.querySelector('#userContent')

    if (!u) {
      console.warn('userContent未渲染')
      return
    }

    this.modalHeight = u.clientHeight

    this.$nextTick(() => {
      this.computedImageList = this.imageList.map(o => this.filterImage(o))
    })
  }
  updated() {}
  destroyed() {
    if (this.emblaApi) {
      this.emblaApi.destroy()
    }
  }
}
